import React from 'react';
import { Link } from 'react-router-dom';
import { gql, useQuery } from '@apollo/client';
import { readRecent } from './recentStorage';
import {
  RecentEntry,
  RecentTranslationsProps,
  TranslationSummary,
} from './RecentTranslations.types';

const TRANSLATION_SUMMARY = gql`
  query RecentDreamImportSet($id: ID!) {
    dreamImportSet(id: $id) {
      id
      status
      outputs {
        id
        status
      }
    }
  }
`;

const filesLabel = (files: string[]) =>
  files.length === 1 ? files[0] : `${files[0]} and ${files.length - 1} more`;

const Row: React.FC<{ entry: RecentEntry; pendingOnly: boolean }> = ({
  entry,
  pendingOnly,
}) => {
  const { data } = useQuery<TranslationSummary>(TRANSLATION_SUMMARY, {
    variables: { id: String(entry.id) },
    fetchPolicy: 'network-only',
  });
  const outputs = data?.dreamImportSet.outputs;
  // Not found here (another location) or still loading: nothing to show yet.
  if (!outputs) return null;
  const done = outputs.filter((output) => output.status === 'imported').length;
  const finished = outputs.length > 0 && done === outputs.length;
  if (pendingOnly && (finished || !outputs.length)) return null;

  return (
    <li className='dreamImportRecentRow'>
      <Link to={`/importWizard/translate/${entry.id}`}>
        <span className='files'>{filesLabel(entry.files)}</span>
        <span className='when'>
          {new Date(entry.at).toLocaleString([], {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </span>
        <span className={`chip ${finished ? 'done' : 'pending'}`}>
          {finished ? 'All imported' : `${done} of ${outputs.length} imported`}
        </span>
      </Link>
    </li>
  );
};

// The translations dropped from this browser, each with how many of its imports are done, linking back to its cards.
const RecentTranslations: React.FC<RecentTranslationsProps> = ({
  businessId,
  pendingOnly = false,
  title = 'Your translated imports',
}) => {
  const entries = readRecent(businessId);
  if (!entries.length) return null;

  return (
    <section className='dreamImportRecent' id='dream_import_recent'>
      <h3>{title}</h3>
      <ul>
        {entries.map((entry) => (
          <Row key={entry.id} entry={entry} pendingOnly={pendingOnly} />
        ))}
      </ul>
    </section>
  );
};

export default RecentTranslations;
